// ============================================================
// Public Form Page - Candidate-facing application form
// ============================================================

import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useState, useRef, useEffect } from 'react';
import toast from 'react-hot-toast';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export function PublicFormPage() {
    const { token } = useParams();
    const [formData, setFormData] = useState<Record<string, any>>({ name: '', email: '', phone: '' });
    const [submitted, setSubmitted] = useState(false);
    const [selectedJobId, setSelectedJobId] = useState('');
    const fileInputs = useRef<Record<string, File>>({});

    const { data, isLoading, error } = useQuery({
        queryKey: ['public-form', token],
        queryFn: async () => {
            const res = await fetch(`${API_BASE}/public/forms/${token}`);
            if (!res.ok) throw new Error('Form not found');
            return res.json();
        },
    });

    const { data: jobsData } = useQuery({
        queryKey: ['public-form-jobs', token],
        queryFn: async () => {
            const res = await fetch(`${API_BASE}/public/forms/${token}/jobs`);
            if (!res.ok) throw new Error('Failed to fetch positions');
            return res.json();
        },
    });

    const jobs = jobsData?.data || [];

    useEffect(() => {
        if (jobs.length === 1) {
            setSelectedJobId(jobs[0].id);
        }
    }, [jobs]);

    const submitMutation = useMutation({
        mutationFn: async () => {
            const fd = new FormData();
            fd.append('name', formData.name);
            fd.append('email', formData.email);
            fd.append('phone', formData.phone || '');
            if (selectedJobId) {
                fd.append('jobId', selectedJobId);
            }
            fd.append('data', JSON.stringify({ ...formData, selectedJobId }));

            Object.entries(fileInputs.current).forEach(([key, file]) => {
                fd.append(key, file);
            });

            const res = await fetch(`${API_BASE}/public/forms/${token}/submit`, {
                method: 'POST',
                body: fd,
            });

            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.error || 'Submission failed');
            }
            return res.json();
        },
        onSuccess: () => {
            setSubmitted(true);
            toast.success('Application submitted!');
        },
        onError: (err: any) => toast.error(err.message),
    });

    if (isLoading) return <div className="loading-page"><div className="loading-spinner" /></div>;
    if (error) return (
        <div className="loading-page">
            <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '3rem', marginBottom: 16 }}>🔗</div>
                <h2 style={{ marginBottom: 8 }}>Form Not Found</h2>
                <p style={{ color: '#6b7280' }}>This link may have expired or the form is no longer published.</p>
            </div>
        </div>
    );

    const formInfo = data?.data;
    const schema = formInfo?.schema;

    if (submitted) return (
        <div className="loading-page">
            <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '3rem', marginBottom: 16 }}>✅</div>
                <h2 style={{ marginBottom: 8, fontWeight: 800 }}>Application Submitted!</h2>
                <p style={{ color: '#4b5563', maxWidth: 400 }}>
                    Thank you for applying{formInfo?.tenant?.name ? ` to ${formInfo.tenant.name}` : ''}. We'll review your application and get back to you soon.
                </p>
            </div>
        </div>
    );

    return (
        <div className="candidate-form animate-fadeIn">
            <div className="form-header">
                {formInfo?.tenant?.name && (
                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: 8 }}>{formInfo.tenant.name}</div>
                )}
                <h1 style={{ fontSize: '1.8rem', fontWeight: 800, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                    {formInfo?.name || 'Application Form'}
                </h1>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); submitMutation.mutate(); }}>
                {/* Basic Info */}
                <div className="form-section">
                    <h2>Personal Information</h2>
                    <p className="section-description">Please provide your contact details</p>
                    <div className="form-group">
                        <label className="form-label">Full Name *</label>
                        <input className="form-input" value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                    </div>
                    <div className="form-group">
                        <label className="form-label">Email *</label>
                        <input className="form-input" type="email" value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
                    </div>
                    <div className="form-group">
                        <label className="form-label">Phone</label>
                        <input className="form-input" type="tel" value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                    </div>
                </div>

                {/* Job Selection Section */}
                {jobs.length > 0 && (
                    <div className="form-section animate-fadeIn">
                        <h2>Position Details</h2>
                        <p className="section-description">Select the post you are applying for</p>
                        <div className="form-group">
                            <label className="form-label">Position Applying For *</label>
                            {jobs.length === 1 ? (
                                <input className="form-input" value={jobs[0].title} disabled style={{ background: 'var(--color-bg-tertiary)', cursor: 'not-allowed' }} />
                            ) : (
                                <select className="form-select" value={selectedJobId} onChange={(e) => setSelectedJobId(e.target.value)} required
                                    style={{
                                        borderColor: '#818cf8',
                                        borderWidth: '2px',
                                        boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.15)',
                                        color: '#4f46e5',
                                        fontWeight: 600,
                                        backgroundColor: '#f5f3ff',
                                        padding: '10px 14px'
                                    }}>
                                    <option value="" style={{ color: '#6b7280' }}>Select a position...</option>
                                    {jobs.map((j: any) => (
                                        <option key={j.id} value={j.id} style={{ color: 'var(--color-text-primary)' }}>{j.title}</option>
                                    ))}
                                </select>
                            )}
                        </div>
                    </div>
                )}

                {jobs.length === 0 && (
                    <div className="form-section animate-fadeIn" style={{ border: '1px solid #ef4444', backgroundColor: 'rgba(239, 68, 68, 0.05)' }}>
                        <h2 style={{ color: '#ef4444' }}>No Active Positions</h2>
                        <p style={{ color: '#ef4444', fontSize: '13px' }}>There are currently no active positions accepting applications under this form. You cannot submit an application at this time.</p>
                    </div>
                )}

                {/* Dynamic sections from schema */}
                {schema?.sections?.filter((section: any) => section.id !== '1' && section.title !== 'Personal Information').map((section: any, si: number) => (
                    <div key={si} className="form-section">
                        <h2>{section.title}</h2>
                        {section.description && <p className="section-description">{section.description}</p>}
                        {section.fields?.map((field: any) => (
                            <div key={field.id} className="form-group">
                                <label className="form-label">{field.label} {field.required && '*'}</label>
                                {field.description && (
                                    <p style={{ fontSize: '12px', color: '#4b5563', margin: '4px 0 8px', fontStyle: 'italic', lineHeight: 1.5 }}>
                                        {field.description}
                                    </p>
                                )}
                                {field.type === 'textarea' ? (
                                    <textarea className="form-textarea"
                                        value={formData[field.id] || ''}
                                        onChange={(e) => setFormData({ ...formData, [field.id]: e.target.value })}
                                        required={field.required} />
                                ) : field.type === 'file' ? (
                                    <input type="file" className="form-input" style={{ padding: 8 }}
                                        onChange={(e) => {
                                            if (e.target.files?.[0]) fileInputs.current[field.id] = e.target.files[0];
                                        }} required={field.required} />
                                ) : field.type === 'select' ? (
                                    <select className="form-select"
                                        value={formData[field.id] || ''}
                                        onChange={(e) => setFormData({ ...formData, [field.id]: e.target.value })}
                                        required={field.required}>
                                        <option value="">Select...</option>
                                        {(field.options || []).map((opt: string) => <option key={opt} value={opt}>{opt}</option>)}
                                    </select>
                                ) : field.type === 'radio' ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        {(field.options || []).map((opt: string, i: number) => (
                                            <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '14px', cursor: 'pointer' }}>
                                                <input type="radio" name={field.id}
                                                    value={opt}
                                                    checked={formData[field.id] === opt}
                                                    onChange={(e) => setFormData({ ...formData, [field.id]: e.target.value })}
                                                    required={field.required} />
                                                {opt}
                                            </label>
                                        ))}
                                    </div>
                                ) : field.type === 'checkbox' ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        {(field.options || []).map((opt: string, i: number) => (
                                            <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '14px', cursor: 'pointer' }}>
                                                <input type="checkbox"
                                                    checked={(formData[field.id] || []).includes(opt)}
                                                    onChange={(e) => {
                                                        const current: string[] = formData[field.id] || [];
                                                        const updated = e.target.checked
                                                            ? [...current, opt]
                                                            : current.filter((v: string) => v !== opt);
                                                        setFormData({ ...formData, [field.id]: updated });
                                                    }} />
                                                {opt}
                                            </label>
                                        ))}
                                    </div>
                                ) : field.type === 'table' ? (
                                    <div style={{ overflowX: 'auto' }}>
                                        <table className="data-table" style={{ width: '100%' }}>
                                            <thead>
                                                <tr>
                                                    {(field.columns || []).map((col: string, i: number) => (
                                                        <th key={i}>{col}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {Array.from({ length: (formData[field.id]?.length || field.minRows || 1) }).map((_, ri) => (
                                                    <tr key={ri}>
                                                        {(field.columns || []).map((col: string, ci: number) => (
                                                            <td key={ci}>
                                                                 <input className="form-input"
                                                                     style={{ background: 'transparent', border: '1px solid var(--color-border)', width: '100%' }}
                                                                     value={(formData[field.id]?.[ri]?.[col]) || ''}
                                                                     onChange={(e) => {
                                                                         const rows = [...(formData[field.id] || Array.from({ length: field.minRows || 1 }, () => ({})))];
                                                                         if (!rows[ri]) rows[ri] = {};
                                                                         rows[ri][col] = e.target.value;
                                                                         setFormData({ ...formData, [field.id]: rows });
                                                                     }}
                                                                     placeholder={col} />
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {field.maxRows && (formData[field.id]?.length || field.minRows || 1) < field.maxRows && (
                                            <button type="button" className="btn btn-ghost" style={{ marginTop: 8, fontSize: '13px' }}
                                                onClick={() => {
                                                    const rows = [...(formData[field.id] || Array.from({ length: field.minRows || 1 }, () => ({})))];
                                                    rows.push({});
                                                    setFormData({ ...formData, [field.id]: rows });
                                                }}>
                                                + Add Row
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <input className="form-input" type={field.type || 'text'}
                                        value={formData[field.id] || ''}
                                        onChange={(e) => setFormData({ ...formData, [field.id]: e.target.value })}
                                        required={field.required} />
                                )}
                            </div>
                        ))}
                    </div>
                ))}

                <button type="submit" className="btn btn-primary btn-lg w-full" disabled={submitMutation.isPending || jobs.length === 0 || (jobs.length > 1 && !selectedJobId)}
                    style={{ marginTop: 16 }}>
                    {submitMutation.isPending ? 'Submitting...' : 'Submit Application'}
                </button>
            </form>
        </div>
    );
}
